import { UserData } from '../contexts/UserContext';
import { Student } from '../types/Student';

/**
 * Transforms UserContext data to match the Student interface structure
 * This allows components expecting Student data to work with UserContext data
 */
export function userToStudentAdapter(user: UserData | null): Student | null {
  if (!user) return null;

  // Transform courses from UserContext format to Student.grades format
  const grades: Record<string, {
    Prerequisite: string;
    courseType: string;
    grade: string;
    gradeType: string;
    grades: string;
    level: string;
    name: string;
    segment: string;
  }> = {};

  // Convert UserContext.courses to Student.grades format
  Object.entries(user.courses).forEach(([courseCode, courseData]) => {
    grades[courseCode] = {
      Prerequisite: courseCode,
      courseType: 'academic',
      grade: courseData.grade, // This should be the grade level (12, 11, 10)
      gradeType: courseData.gradeType || 'actual', // Use the stored grade type or default to 'actual'
      grades: courseData.grades.toString(), // This should be the actual grade value
      level: courseData.grade, // This should also be the grade level
      name: courseCode,
      segment: 'academic'
    };
  });

  // Map profileExtra data to Student interface
  const profileExtra = user.profileExtra || {};
  
  // Transform work & volunteering data
  const workVolunteerExpProfile: Record<string, {
    position?: string;
    employer?: string;
    startGrade?: string;
    endGrade?: string;
    description?: string;
  }> = {};
  
  if (profileExtra.work_VolunteerExpProfile) {
    Object.entries(profileExtra.work_VolunteerExpProfile).forEach(([key, data]) => {
      workVolunteerExpProfile[key] = {
        position: data.position,
        employer: data.employer,
        // Handle both startDate/endDate (new format) and startGrade/endGrade (old format) for backward compatibility
        startGrade: (data as any).startDate || (data as any).startGrade,
        endGrade: (data as any).endDate || (data as any).endGrade,
        description: data.description
      };
    });
  }
  
  // Transform athletics data
  const athleticsSportsProfile: Record<string, {
    startGrade?: string;
    endGrade?: string;
    position?: string;
    description?: string;
    jerseyNumber?: string;
    level?: string;
    youtubeLink?: string;
    articleLink?: string;
    isContinue?: number | string;
  }> = {};
  
  if (profileExtra.athleticsSportsProfile) {
    Object.entries(profileExtra.athleticsSportsProfile).forEach(([key, data]) => {
      athleticsSportsProfile[key] = {
        startGrade: data.startGrade,
        endGrade: data.endGrade,
        position: data.position,
        description: data.description,
        jerseyNumber: data.jerseyNumber,
        level: data.level,
        youtubeLink: data.youtubeLink,
        articleLink: data.articleLink,
        isContinue: data.isContinue ? 1 : 0
      };
    });
  }
  
  // Transform academic awards & honours data
  const academicAwardsHonoursProfile: Record<string, {
    title?: string;
    grade?: string;
    yearReceived?: string;
    description?: string;
  }> = {};
  
  if (profileExtra.academicAwardsHonoursProfile) {
    Object.entries(profileExtra.academicAwardsHonoursProfile).forEach(([key, data]) => {
      academicAwardsHonoursProfile[key] = {
        title: data.title,
        grade: data.grade,
        yearReceived: data.yearReceived,
        description: data.description
      };
    });
  }
  
  // Transform academic extenuating circumstances data
  const academicExtenuatingCircumstancesProfile: Record<string, {
    description?: string;
  }> = {};
  
  if (profileExtra.academicExtenuatingCircumstancesProfile) {
    Object.entries(profileExtra.academicExtenuatingCircumstancesProfile).forEach(([key, data]) => {
      academicExtenuatingCircumstancesProfile[key] = {
        description: data.description
      };
    });
  }
  
  // Transform interests & activities data
  const interestsActivitiesProfile: Record<string, {
    interest?: string;
    startGrade?: string;
    endGrade?: string;
    description?: string;
    link?: string;
  }> = {};
  
  if (profileExtra.interests_ActivitiesProfile) {
    Object.entries(profileExtra.interests_ActivitiesProfile).forEach(([key, data]) => {
      interestsActivitiesProfile[key] = {
        interest: data.interest,
        startGrade: data.startGrade,
        endGrade: data.endGrade,
        description: data.description,
        link: data.link
      };
    });
  }
  
  // Create Student object with UserContext data
  const student: Student = {
    id: user.uid,
    firstName: user.firstName,
    lastName: user.lastName,
    grade: user.classYear || '',
    school: user.schoolName,
    city: user.city,
    province: user.province,
    interests: [], // Will be populated from other sources if needed
    favoriteSchools: [], // Will be populated from other sources if needed
    achievements: [], // Will be populated from other sources if needed
    gpa: 0, // Will be calculated if needed
    email: user.email,
    phone: user.phoneNumber,
    status: user.status as 'Active' | 'Inactive' | 'Graduated' | 'Researching Undergraduate Options' || 'Active',
    enrollmentDate: '', // Will be populated from other sources if needed
    profileImage: user.userIcon || undefined,
    grades,
    // Grade averages from UserContext
    grade12Average: user.grade12Average,
    grade12Top6Average: user.grade12Top6Average,
    // Additional fields with fallback values
    favoritePrograms: [],
    favoriteProgramCategories: [],
    completedCourses: [],
    clubs: [],
    athletics: [],
    activities: [],
    workExperience: [],
    registeredEventIds: [],
    athleticsSportsProfile,
    athleticsAwardsProfile: {},
    academicAwardsHonoursProfile,
    academicExtenuatingCircumstancesProfile,
    workVolunteerExpProfile,
    interestsActivitiesProfile,
    profileCompletion: '',
    eligibilityReport: {}
  };

  return student;
}

/**
 * Creates a mock StudentsContext-like object using UserContext data
 * This maintains compatibility with components expecting StudentsContext structure
 */
export function createMockStudentsContext(user: UserData | null) {
  const student = userToStudentAdapter(user);
  
  return {
    students: student ? [student] : [],
    loading: false,
    error: null,
    refresh: () => {} // No-op since UserContext handles real-time updates
  };
}
